'use client';

export interface ThreeYearDerivation {
  symbol: string;
  currentPrice: number | null;
  threeYearTarget: number | null;
  fy1Eps?: number | null;
  fy3Eps?: number | null;
  targetMultiple?: { peerMedian?: number | null; ownMedian?: number | null; nearTerm?: number | null } | null;
  growth?: { gLtg?: number | null; g3y?: number | null; g5y?: number | null; median?: number | null; final?: number | null; qualityFade?: number | null } | null;
  calibrated3Y?: number | null;
  dcfFairValue?: number | null;
}

const fmtPx = (v: number | null | undefined) => (v == null ? '—' : `$${v.toFixed(2)}`);

function priceDelta(target: number | null, current: number | null): string {
  if (target == null || current == null || current <= 0) return '';
  const pct = (target / current - 1) * 100;
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(0)}%`;
}

function Row({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="flex justify-between items-baseline text-[10px]">
      <span className={highlight ? 'text-[#94a3b8] font-semibold' : 'text-[#64748b]'}>{label}</span>
      <span className={`tabular-nums font-mono ${highlight ? 'text-white font-semibold' : 'text-[#64748b]'}`}>{value}</span>
    </div>
  );
}

export function ThreeYearModal({ d, onClose }: { d: ThreeYearDerivation; onClose: () => void }) {
  const { symbol, currentPrice, threeYearTarget, fy1Eps, fy3Eps, targetMultiple: mult, growth: g, calibrated3Y, dcfFairValue } = d;
  return (
    <div className="fixed inset-0 z-[3000] flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70" />
      <div
        className="relative z-10 bg-[#0a0e14] border border-[#1e293b] rounded-xl p-6 w-[440px] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-[#64748b] mb-0.5">3-Year Target · Derivation</div>
            <div className="text-[18px] font-bold text-white">{symbol} — {fmtPx(threeYearTarget)}</div>
            {currentPrice != null && (
              <div className="text-[11px] text-[#64748b]">
                vs current {fmtPx(currentPrice)} ({priceDelta(threeYearTarget, currentPrice)})
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-[#475569] hover:text-white text-xl leading-none ml-4">×</button>
        </div>

        <div className="text-[10px] text-[#64748b] bg-[#0d1117] border border-[#1e293b] rounded px-3 py-2 mb-4 font-mono">
          3-Year Target = FY+3 EPS × Applied Multiple
          {fy3Eps != null && mult?.nearTerm != null && (
            <span className="ml-2 text-[#94a3b8]">
              = ${fy3Eps.toFixed(2)} × {mult.nearTerm.toFixed(2)}× = {fmtPx(threeYearTarget)}
            </span>
          )}
        </div>

        <div className="mb-4">
          <div className="text-[9px] font-semibold uppercase tracking-widest text-[#475569] mb-2">Applied Multiple (P/E)</div>
          <div className="space-y-1">
            {mult?.peerMedian != null && <Row label="Peer group median" value={`${mult.peerMedian.toFixed(2)}×`} />}
            {mult?.ownMedian != null && <Row label="5yr own-history median" value={`${mult.ownMedian.toFixed(2)}×`} />}
            {mult?.nearTerm != null && <Row label="Applied blend" value={`${mult.nearTerm.toFixed(2)}×`} highlight />}
          </div>
        </div>

        <div className="mb-4">
          <div className="text-[9px] font-semibold uppercase tracking-widest text-[#475569] mb-2">EPS Growth Path → FY+3</div>
          <div className="space-y-1">
            {g?.gLtg != null && <Row label="BBG LTG consensus" value={`${(g.gLtg * 100).toFixed(1)}%`} />}
            {g?.g3y != null && <Row label="BBG 3yr consensus" value={`${(g.g3y * 100).toFixed(1)}%`} />}
            {g?.g5y != null && <Row label="BBG 5yr consensus" value={`${(g.g5y * 100).toFixed(1)}%`} />}
            {g?.median != null && <Row label="Median of above" value={`${(g.median * 100).toFixed(1)}%`} />}
            {g?.qualityFade != null && <Row label={`Quality fade ×${g.qualityFade.toFixed(2)}`} value="" />}
            {g?.final != null && <Row label="Final rate applied" value={`${(g.final * 100).toFixed(1)}%`} highlight />}
          </div>
        </div>

        <div className="mb-4">
          <div className="text-[9px] font-semibold uppercase tracking-widest text-[#475569] mb-2">Earnings Per Share</div>
          <div className="space-y-1">
            {fy1Eps != null && <Row label="FY+1 EPS (BBG 1BF consensus)" value={fmtPx(fy1Eps)} />}
            {fy3Eps != null && <Row label="FY+3 EPS (BBG 3BF consensus)" value={fmtPx(fy3Eps)} highlight />}
          </div>
        </div>

        <div className="border-t border-[#1e293b] pt-3 flex items-center justify-between">
          <div className="text-[10px] text-[#64748b]">3-Year Target Price</div>
          <div className="text-[18px] font-bold text-white tabular-nums">{fmtPx(threeYearTarget)}</div>
        </div>

        {dcfFairValue != null && (
          <div className="mt-3 pt-3 border-t border-[#1e293b]">
            <div className="text-[9px] font-semibold uppercase tracking-widest text-[#475569] mb-1">DCF Intrinsic Value (reference)</div>
            <div className="flex justify-between items-baseline text-[10px]">
              <span className="text-[#64748b]">10-yr DCF, discounted to today</span>
              <span className="tabular-nums font-mono text-[#64748b]">{fmtPx(dcfFairValue)}{currentPrice != null && currentPrice > 0 ? ` (${priceDelta(dcfFairValue, currentPrice)})` : ''}</span>
            </div>
            <div className="text-[8px] text-[#334155] mt-1">DCF = today&apos;s intrinsic value, not a 3yr price target — shown for context only.</div>
          </div>
        )}
        {calibrated3Y != null && (
          <div className="mt-2 text-[9px] text-[#475569]">
            Regression-calibrated 3yr: {fmtPx(calibrated3Y)} (reference only — base target used above).
          </div>
        )}
      </div>
    </div>
  );
}
